import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { formatMoney } from '../cashbacks/formatMoney';
import {
  CURRENCY_BY_MARKET,
  ADJUSTMENT_REASON_CODES,
  type User,
} from '../dataProvider/types';
import {
  ADJUSTMENT_CAP,
  ADJUSTMENT_DIRECTION_LABEL,
  ADJUSTMENT_REASON_LABEL,
  type AdjustmentDirection,
} from './adjustmentsState';

/**
 * Adjust a member's balance (D-balance-adjustment, the Phase 5 capstone): the most
 * sensitive write in the admin, value moved directly by a super_admin with no
 * underlying event. It is super_admin-only (capability balances:adjust, covered only
 * by '*'); UserShow hides it from everyone else and this component guards the write
 * again before it commits.
 *
 * The adjustment is a ledger-entry CREATE, NEVER a direct balance write: it creates a
 * balanceLedger row (kind manual_adjustment, a SIGNED amount, transactionId null) that
 * deriveBalanceSigned adds, and the balance re-derives. The amount is signed by the
 * chosen direction (a credit positive, a debit negative). A mandatory reason code and
 * note ride in the entry, so both land in the audit trail (the write is audited for
 * free by the wrapper). The context shows balanceSigned (the true figure), not the
 * floored member-facing balance, so a debit driving the wallet negative is honest.
 *
 * GUARDRAIL: a per-adjustment cap (D-balance-adjustment). An adjustment whose
 * magnitude reaches ADJUSTMENT_CAP goes through a deliberate confirmation that shows
 * the figure before it commits, catching the high-volume fat-finger. There is no
 * separate override capability; the confirm IS the friction.
 */
export const AdjustBalanceAction = ({ user }: { user: User }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [createdAtMs] = useState<number>(() => Date.now());

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<'form' | 'confirm'>('form');
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState<AdjustmentDirection>('credit');
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  const currency = CURRENCY_BY_MARKET[user.market];
  const cap = ADJUSTMENT_CAP[user.market];
  const magnitude = Number(amount);
  const magnitudeValid = Number.isFinite(magnitude) && magnitude > 0;
  const signed =
    Math.round((direction === 'debit' ? -magnitude : magnitude) * 100) / 100;
  const before = Number(user.balanceSigned ?? 0);
  const after = Math.round((before + signed) * 100) / 100;
  const overCap = magnitudeValid && magnitude >= cap;
  const canSubmit =
    magnitudeValid && reasonCode.trim().length > 0 && note.trim().length > 0;

  const reset = () => {
    setPhase('form');
    setDirection('credit');
    setAmount('');
    setReasonCode('');
    setNote('');
  };

  const close = () => {
    setOpen(false);
    setLoading(false);
    reset();
  };

  const doCreate = async () => {
    setLoading(true);
    try {
      if (
        !hasAccess(
          { role, accessibleRegions: [] },
          { capability: 'balances:adjust' },
        )
      ) {
        throw new Error('Not permitted');
      }
      // The adjustment: a signed ledger entry the balance derives from. NOT a direct
      // balance write and tied to no transaction.
      await dataProvider.create('balanceLedger', {
        data: {
          userId: user.id,
          transactionId: null,
          amount: signed,
          market: user.market,
          kind: 'manual_adjustment',
          reasonCode,
          reason: note.trim(),
          createdAt: new Date(createdAtMs).toISOString(),
        },
      });
      notify('Balance adjusted', { type: 'success' });
      close();
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      close();
    }
  };

  const onSubmit = () => {
    if (!canSubmit) return;
    if (overCap) {
      setPhase('confirm');
      return;
    }
    void doCreate();
  };

  return (
    <>
      <Button variant="outlined" color="warning" onClick={() => setOpen(true)}>
        Adjust balance
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Adjust {user.name}&apos;s balance</DialogTitle>
        {phase === 'form' ? (
          <>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Move value directly on this member&apos;s wallet. This records a
                ledger entry the balance derives from, never a direct change to
                a balance. Choose the direction, the amount, a reason and a
                note.
              </DialogContentText>
              <Box
                data-testid="adjustment-current-balance"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Current balance: {formatMoney(before, currency)}
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id={`adj-direction-${user.id}`}>
                  Direction
                </InputLabel>
                <Select
                  labelId={`adj-direction-${user.id}`}
                  label="Direction"
                  value={direction}
                  inputProps={{ 'data-testid': 'adjustment-direction' }}
                  onChange={(e) =>
                    setDirection(e.target.value as AdjustmentDirection)
                  }
                >
                  <MenuItem value="credit">
                    {ADJUSTMENT_DIRECTION_LABEL.credit}
                  </MenuItem>
                  <MenuItem value="debit">
                    {ADJUSTMENT_DIRECTION_LABEL.debit}
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                error={amount.length > 0 && !magnitudeValid}
                helperText={`In ${currency.toUpperCase()}. A magnitude at or above ${formatMoney(cap, currency)} needs a second confirmation.`}
                inputProps={{ 'data-testid': 'adjustment-amount' }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id={`adj-reason-${user.id}`}>
                  Reason code
                </InputLabel>
                <Select
                  labelId={`adj-reason-${user.id}`}
                  label="Reason code"
                  value={reasonCode}
                  inputProps={{ 'data-testid': 'adjustment-reason-code' }}
                  onChange={(e) => setReasonCode(e.target.value)}
                >
                  {ADJUSTMENT_REASON_CODES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {ADJUSTMENT_REASON_LABEL[code]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Justification"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="Recorded with the action, for the audit trail"
              />
              {magnitudeValid && (
                <Box
                  data-testid="adjustment-projected-balance"
                  sx={{
                    mt: 2,
                    color: after < 0 ? 'error.main' : 'text.primary',
                  }}
                >
                  New balance after: {formatMoney(after, currency)}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={close}>Cancel</Button>
              <Button
                variant="contained"
                color="warning"
                disabled={!canSubmit || loading}
                onClick={onSubmit}
              >
                Confirm
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <DialogContentText
                data-testid="adjustment-large-confirm"
                sx={{ mb: 2 }}
              >
                You are about to {direction} {user.name}&apos;s balance by{' '}
                {formatMoney(Math.abs(signed), currency)}. The new balance will
                be {formatMoney(after, currency)}. This is a large adjustment.
                Confirm it is correct.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPhase('form')}>Back</Button>
              <Button
                variant="contained"
                color="warning"
                disabled={loading}
                onClick={() => void doCreate()}
              >
                Confirm large adjustment
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
